import { useId, useRef, useState } from "react";
import { Form, Link, useActionData, useFetcher, useNavigation, useParams } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { Tenant, TenantDeletion } from "@repo/shared-types";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { tenantPath } from "@/lib/tenants";
import { SpacePicture } from "@/components/avatar";
import { ErrorNote, PictureButton, SegmentedControl } from "@/components/form";
import { PictureFrame } from "@/components/picture-frame";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  ActionNotes,
  SectionHeader,
  backToSpace,
  counted,
  loadSection,
  useSection,
  type ActionResult,
} from "@/routes/space-section";

/**
 * A space's settings — its picture, its name, who can find it — and deleting
 * it: the owner's alone, whatever an admin holds. There are no units to set
 * since 19 October: the app is metric only.
 *
 * Who can find it is a tap, saved the moment it is tapped and said by a
 * toast (decided with Tiago 12 October); the name keeps its box and its
 * button, since a half-typed name is not one to save.
 *
 * Deleting the space says what it takes before anything is pressed, as
 * closing an account does: how many other people lose it, and what goes
 * with it, counted by the api for the owner alone.
 */

export async function settingsLoader({ request, params }: LoaderFunctionArgs) {
  const section = await loadSection(request, params, "settings");
  if (!section.isOwner || section.tenant.isPersonal) throw backToSpace(section);
  await read(queries.spaceDeletion(params.slug ?? ""));
  return null;
}

export default function SpaceSettings() {
  const slug = useParams().slug ?? "";
  const { tenant, images } = useSection(slug);
  const deletion = useSuspenseQuery(queries.spaceDeletion(slug)).data;
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";

  return (
    <section className="space-y-6">
      <SectionHeader tenant={tenant} title="Settings" />
      {/* The picture's and deleting's forms, when what went wrong names no box. */}
      <ActionNotes result={result} />

      {/* Keyed by the picture, so a save starts the box afresh. */}
      {images && <PictureField key={tenant.image ?? ""} tenant={tenant} busy={busy} />}

      <Card>
        <CardContent className="space-y-8">
          <RenameField tenant={tenant} />
          <VisibilityField tenant={tenant} />
        </CardContent>
      </Card>

      <DeleteSpace tenant={tenant} deletion={deletion} result={result} busy={busy} />
    </section>
  );
}

/**
 * Deleting the space, with what goes counted first. Typing the name is the
 * confirmation; with anybody else in it, so is a tick for them, keyed by
 * their count so a refusal after somebody joined asks for it again.
 */
function DeleteSpace({
  tenant,
  deletion,
  result,
  busy,
}: {
  tenant: Tenant;
  deletion: TenantDeletion;
  result: ActionResult | undefined;
  busy: boolean;
}) {
  const forThem = useId();
  return (
    <Card className="ring-destructive/30 dark:ring-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">
          <h2>Delete this space</h2>
        </CardTitle>
        <CardDescription>It cannot be undone. Going with it, for everyone:</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
          <li>{counted(deletion.events, "event", "events")} and their leaderboards</li>
          <li>{counted(deletion.sharedResults, "result", "results")} shared on them</li>
          <li>{counted(deletion.comments, "comment", "comments")}</li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Nobody's results are deleted: each stays in its athlete's own log,
          and only stops being shown here.
        </p>

        <Form method="post" className="space-y-4">
          <input type="hidden" name="intent" value="delete" />
          <input type="hidden" name="name" value={tenant.name} />
          <input type="hidden" name="otherMembers" value={deletion.otherMembers} />

          {deletion.otherMembers === 0 ? (
            <p className="text-sm text-muted-foreground">You are its only member.</p>
          ) : (
            // Keyed by the count, so a different one — somebody joined, and
            // the api refused — asks for the tick again rather than
            // keeping one given for fewer people.
            <div key={deletion.otherMembers} className="rounded-2xl border p-4">
              <FieldSet className="min-w-0 gap-3">
                <FieldLegend variant="label" className="mb-1.5">
                  {deletion.otherMembers === 1
                    ? "1 other person is a member"
                    : `${deletion.otherMembers.toLocaleString()} other people are members`}
                </FieldLegend>
                <FieldDescription>
                  They lose it with you: its events, its boards and its
                  conversation. If it should carry on without you, hand it to
                  one of them instead.
                </FieldDescription>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Field
                    orientation="horizontal"
                    data-invalid={result?.field === "otherMembers" ? true : undefined}
                    className="w-auto min-w-0 flex-1 basis-48"
                  >
                    {/* Inside the form, so the checkbox sends `forThem=on` with it when ticked. */}
                    <Checkbox
                      id={forThem}
                      name="forThem"
                      aria-invalid={result?.field === "otherMembers" ? true : undefined}
                    />
                    <FieldLabel htmlFor={forThem}>
                      Delete it for the{" "}
                      {counted(deletion.otherMembers, "other member", "other members")} too
                    </FieldLabel>
                  </Field>
                  {/* Handing it on is the member's sheet's, on the members page. */}
                  <Button asChild variant="outline" size="sm">
                    <Link to={`${tenantPath(tenant)}/members`}>Hand it on</Link>
                  </Button>
                </div>
                {result?.field === "otherMembers" && <FieldError>{result.error}</FieldError>}
              </FieldSet>
            </div>
          )}

          <Field data-invalid={result?.field === "confirm" ? true : undefined} className="gap-2">
            <div className="flex gap-2">
              <Input
                name="confirm"
                autoComplete="off"
                placeholder={tenant.name}
                aria-label={`Type ${tenant.name} to confirm`}
                aria-invalid={result?.field === "confirm" ? true : undefined}
                className="flex-1"
              />
              <Button type="submit" variant="destructive" disabled={busy}>
                Delete
              </Button>
            </div>
            {result?.field === "confirm" ? (
              <FieldError>{result.error}</FieldError>
            ) : (
              <FieldDescription>
                Type <strong className="font-medium break-words text-foreground">{tenant.name}</strong> to confirm.
              </FieldDescription>
            )}
          </Field>
        </Form>
      </CardContent>
    </Card>
  );
}

/**
 * The name, in its box, saved by Rename. By fetcher, so what goes wrong is
 * said beneath the box and what works is the action's toast; the header
 * reads the new name as the page's loaders read the space again.
 */
function RenameField({ tenant }: { tenant: Tenant }) {
  const fetcher = useFetcher<ActionResult>();
  const id = useId();
  const error = fetcher.data?.error;
  return (
    <fetcher.Form method="post">
      <input type="hidden" name="intent" value="rename" />
      <Field data-invalid={error ? true : undefined} className="gap-2">
        <FieldLabel htmlFor={id}>Name</FieldLabel>
        <div className="flex gap-2">
          <Input
            id={id}
            name="name"
            required
            defaultValue={tenant.name}
            aria-invalid={error ? true : undefined}
            className="flex-1"
          />
          <Button type="submit" variant="outline" disabled={fetcher.state !== "idle"}>
            Rename
          </Button>
        </div>
        {error ? (
          <FieldError>{error}</FieldError>
        ) : (
          <FieldDescription>Renaming it breaks no links: a space's address is its id, never its name.</FieldDescription>
        )}
      </Field>
    </fetcher.Form>
  );
}

type Visibility = "public" | "private";

/**
 * Who can find it, saved on the tap. The tapped one shows while it is on its
 * way, and the line under it follows; refused, the saved one comes back with
 * why beneath.
 */
function VisibilityField({ tenant }: { tenant: Tenant }) {
  const fetcher = useFetcher<ActionResult>();
  const pending = fetcher.formData?.get("visibility");
  const value = (typeof pending === "string" ? pending : tenant.visibility) as Visibility;
  return (
    <div className="space-y-2">
      <SegmentedControl<Visibility>
        label="Visibility"
        name="visibility"
        options={[
          { value: "public", label: "Public" },
          { value: "private", label: "Private" },
        ]}
        value={value}
        onChange={(visibility) => {
          if (visibility !== value) fetcher.submit({ intent: "visibility", visibility }, { method: "post" });
        }}
      />
      <p className="text-sm text-muted-foreground">
        {value === "public" ? "Findable, people can ask to join." : "Invitation only."}
      </p>
      {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
    </div>
  );
}

/**
 * The space's picture (0034): beside its name wherever it is named — its
 * page, every list of spaces, search, invitations, the inbox — or its
 * initials until one is chosen. Framed in a circle, by dragging it, before it
 * is saved; stored square.
 */
function PictureField({ tenant, busy }: { tenant: Tenant; busy: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Picture</h2>
        </CardTitle>
        <CardDescription>
          Shown beside the space's name wherever it appears — here, in everybody's list of spaces, in search, in
          invitations and in the inbox. Without one, its initials are.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-6">
          <Form method="post" encType="multipart/form-data" className="flex flex-wrap items-center gap-6">
            <input type="hidden" name="intent" value="picture" />
            {file ? (
              <div className="w-48">
                <PictureFrame file={file} aspect="1:1" round />
              </div>
            ) : (
              <SpacePicture
                space={tenant}
                size="lg"
                alt={tenant.image ? "The space's picture" : "The space's initials"}
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <PictureButton
                name="file"
                disabled={busy}
                inputRef={input}
                onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}
              >
                {file ? "Choose another" : tenant.image ? "Replace picture" : "Choose a picture"}
              </PictureButton>
              {file && (
                <>
                  <Button type="submit" disabled={busy}>
                    {busy ? "Saving…" : "Save picture"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      if (input.current) input.current.value = "";
                      setFile(null);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </Form>
          {!file && tenant.image && (
            <Form method="post" className="self-center">
              <input type="hidden" name="intent" value="remove-picture" />
              <Button type="submit" variant="outline" disabled={busy}>
                Remove picture
              </Button>
            </Form>
          )}
        </div>
        {file && <p className="text-sm text-muted-foreground">Drag it to frame it; zoom with the slider.</p>}
      </CardContent>
    </Card>
  );
}
